/**
 * @spec docs/superpowers/specs/2026-10-15-staking-ui-wallet-actions-design.md §7.3
 * @standard docs/standards/staking-ui-wallet-actions.md#errors
 *
 * The error every wallet-actions helper throws for a refusal it decides itself. Provider errors
 * from the wallet prompt are wrapped into it by wrap.ts, with the provider error as cause.
 */
import type { SigningErrorCategory } from '@/lib/signing-errors';
import { getSwitchableChain } from '@/lib/chain-registry';
import type { WalletKind } from '@/lib/wallet-kind';
import type { TxRef } from '@/lib/tx-tracker/types';

/** Copy for every refusal of an account that cannot produce a raw signature. */
export const PHENOM_WALLET_ONLY_MESSAGE =
  'Only your Phenom wallet can sign this step. Use your Phenom wallet, or a wallet without a smart account.';

export type WalletActionPhase = 'prepare' | 'sign' | 'broadcast' | 'confirm';

export type WalletActionErrorCategory =
  | SigningErrorCategory
  | 'unsupported-chain'
  | 'unsupported-account'
  | 'insufficient-gas';

export interface WalletActionErrorInit {
  message: string;
  phase: WalletActionPhase;
  category: WalletActionErrorCategory;
  walletKind: WalletKind;
  requiredChainId: number | null;
  cause: unknown;
  txRef?: TxRef;
}

export class WalletActionError extends Error {
  readonly phase: WalletActionPhase;
  readonly category: WalletActionErrorCategory;
  readonly walletKind: WalletKind;
  readonly requiredChainId: number | null;
  readonly cause: unknown;
  readonly txRef?: TxRef;

  constructor(init: WalletActionErrorInit) {
    super(init.message);
    this.name = 'WalletActionError';
    this.phase = init.phase;
    this.category = init.category;
    this.walletKind = init.walletKind;
    this.requiredChainId = init.requiredChainId;
    this.cause = init.cause;
    if (init.txRef !== undefined) this.txRef = init.txRef;
  }
}

/** Copy for an embedded send whose outcome is unknown: it may already be on the network. */
export function uncertainSendMessage(chainId: number): string {
  const explorer = getSwitchableChain(chainId)?.explorerName ?? 'the block explorer';
  return `This transaction may already have been sent. Check your wallet's activity on ${explorer} before trying again.`;
}

/**
 * An embedded-wallet broadcast whose outcome is unknown (send.ts): it may already be on the
 * network, so nothing may follow it automatically (no cleanup transaction, no retry).
 */
export function isUncertainBroadcast(error: unknown): boolean {
  return (
    error instanceof WalletActionError &&
    error.phase === 'confirm' &&
    error.category === 'unknown' &&
    error.txRef === undefined
  );
}

/**
 * What a failed send left on the network: `submitted` when it was broadcast (a hash came back) or
 * may have been (uncertain), so the form must not offer the same send again; `hash` when known.
 */
export function submittedAttempt(error: unknown): { submitted: boolean; hash: string | null } {
  if (error instanceof WalletActionError && error.txRef !== undefined) {
    return { submitted: true, hash: error.txRef.hash ? error.txRef.hash : null };
  }
  return { submitted: isUncertainBroadcast(error), hash: null };
}
